import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { Z_LAYERS } from "../../windowManager/zLayers";
import { PdfIcons } from "./pdfIcons";
import "./pdfEditor.css";
import { alleFenster } from "../../windowManager/browserFenster/fensterRegister";

/**
 * Tab-Zeile der im Editor-Tab geöffneten Dokumente (User 04.10.2026: „alle
 * Tabs … in einem Tab/PDF und man nicht immer springen muss“) — reine
 * Darstellung, die Liste gehört `PdfDokumentPage`.
 *
 * Klick wechselt, × schließt, ein goldener Punkt heißt „ungespeicherte
 * Änderungen“. Pfeiltasten wandern wie bei echten Tabs, Entf schließt.
 */
export interface DokumentTab {
    id: number;
    name: string;
    /** Ungespeicherte Änderungen im Editor dieses Dokuments. */
    ungespeichert?: boolean;
    bild?: boolean;
}

export const DokumentTabLeiste: React.FC<{
    tabs: DokumentTab[];
    aktiv: number | null;
    onWaehlen: (id: number) => void;
    onSchliessen: (id: number) => void;
}> = ({ tabs, aktiv, onWaehlen, onSchliessen }) => {
    const tastatur = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        if (e.key === "Delete") {
            e.preventDefault();
            onSchliessen(tabs[index].id);
            return;
        }
        if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
        e.preventDefault();
        const n = tabs.length;
        const ziel = tabs[(index + (e.key === "ArrowRight" ? 1 : n - 1)) % n];
        onWaehlen(ziel.id);
        e.currentTarget
            .closest("[role=tablist]")
            ?.querySelector<HTMLElement>(`[data-doktab="${ziel.id}"]`)
            ?.focus();
    };
    return (
        <div className="pdfx pdfx-doktabs" role="tablist" aria-label="Geöffnete Dokumente">
            {tabs.map((t, i) => {
                const istAktiv = t.id === aktiv;
                return (
                    <div key={t.id} className={`pdfx-doktab${istAktiv ? " aktiv" : ""}`}>
                        <button
                            type="button"
                            role="tab"
                            data-doktab={t.id}
                            aria-selected={istAktiv}
                            tabIndex={istAktiv ? 0 : -1}
                            title={t.ungespeichert ? `${t.name} — ungespeicherte Änderungen` : t.name}
                            onClick={() => onWaehlen(t.id)}
                            onKeyDown={(e) => tastatur(e, i)}
                            // Mittelklick schließt, wie im Browser.
                            onAuxClick={(e) => e.button === 1 && onSchliessen(t.id)}
                        >
                            {t.bild ? PdfIcons.bild : PdfIcons.pdf}
                            <span className="pdfx-doktab-name">{t.name}</span>
                            {t.ungespeichert && (
                                <span className="pdfx-doktab-punkt" role="img" aria-label="ungespeicherte Änderungen" />
                            )}
                        </button>
                        <button
                            type="button"
                            className="pdfx-doktab-zu"
                            aria-label={`${t.name} schließen`}
                            title="Schließen"
                            tabIndex={-1}
                            onClick={() => onSchliessen(t.id)}
                        >
                            {PdfIcons.schliessen}
                        </button>
                    </div>
                );
            })}
        </div>
    );
};

/**
 * „Tab schließen?“ bei ungespeicherten Änderungen — eigenes Popup wie
 * `SpeichernPopup` (kein Browser-Dialog, kein Fluent): Speichern, Verwerfen
 * oder Abbrechen. Esc und Klick daneben brechen ab.
 */
export const TabSchliessenPopup: React.FC<{
    name: string;
    onSpeichern: () => void;
    onVerwerfen: () => void;
    onAbbrechen: () => void;
}> = ({ name, onSpeichern, onVerwerfen, onAbbrechen }) => {
    const speichernRef = React.useRef<HTMLButtonElement | null>(null);
    React.useEffect(() => {
        speichernRef.current?.focus();
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            e.stopPropagation();
            onAbbrechen();
        };
        alleFenster.addEventListener("keydown", onKey, true);
        return () => alleFenster.removeEventListener("keydown", onKey, true);
    }, [onAbbrechen]);
    return createPortal(
        <div
            className="pdfx pdfx-scrim"
            style={{ zIndex: Z_LAYERS.ueberFenster }}
            onMouseDown={(e) => e.target === e.currentTarget && onAbbrechen()}
        >
            <div className="pdfx-popup" role="dialog" aria-modal="true" aria-labelledby="pdfx-tabzu-titel">
                <div className="pdfx-popup-kopf">
                    <div className="pdfx-popup-kicker">Tab schließen</div>
                    <h2 id="pdfx-tabzu-titel">Änderungen speichern?</h2>
                </div>
                <div className="pdfx-popup-body">
                    <p className="pdfx-popup-text">
                        „{name}“ hat Änderungen, die noch nicht gespeichert sind. Beim Schließen des Tabs gehen sie
                        verloren.
                    </p>
                </div>
                <div className="pdfx-popup-foot">
                    <button type="button" className="pdfx-btn" onClick={onAbbrechen}>
                        Abbrechen
                    </button>
                    <button type="button" className="pdfx-btn gefahr" onClick={onVerwerfen}>
                        Verwerfen
                    </button>
                    <button ref={speichernRef} type="button" className="pdfx-btn primary" onClick={onSpeichern}>
                        {PdfIcons.speichern}
                        Speichern …
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};
